import { useEffect, useState } from "react";
import { ChangingDirectionType, ChangingParameters } from "../../types";
import { mathCalculationService } from "../../../../shared/services/MathCalculationService";
import { StepChangeSelector } from "../StepChangeSelector";
import {
    usePortfolioSelection,
} from "../../../../shared/contexts/portfolio-context/components/PortfolioContextProvider";
import { OrderState } from "../../../../shared/types/OrderState";

export function OrderSizeSelector({
    parameters
}: {
    parameters: ChangingParameters;
}) {
    const {
        isAllPortfoliosSelected,
    } = usePortfolioSelection();

    const getDefaultStepValue = (): number => {
        return 0.010;
    }

    const getStepValue = (): number => {
        if (parameters.selectedOrder !== null) {
            return parameters.selectedOrder.sizeStep ?? getDefaultStepValue();
        }

        return getDefaultStepValue();
    }

    const [stepValue, setStepValue] = useState<number>(getStepValue());

    useEffect(() => {
        const selectedOrder = parameters.selectedOrder;

        setStepValue(
            selectedOrder !== null
                ? selectedOrder.sizeStep ?? (
                    0.010
                )
                : 0.010
        );
    }, [parameters.selectedOrder]);

    const handleChange = async (direction: ChangingDirectionType, step: number): Promise<void> => {
        const selectedOrder = parameters.selectedOrder;

        if (selectedOrder === null || isAllPortfoliosSelected) {
            return;
        }

        const order = parameters.orders.find(x => x.internalId === selectedOrder.internalId);

        if (order === undefined)
            return;

        switch (direction) {
            case ChangingDirectionType.Next:
                order.size = mathCalculationService.round(order.size + step, 4);
                break;

            case ChangingDirectionType.Previous:
                order.size = mathCalculationService.round(order.size - step, 4);
                break;
        }

        parameters.recalculateOrderFee(order);

        order.setChangedState();

        await parameters.updateOrderInSelectedPortfolio(true, false);
    }

    const handleStepSelectionChange = async (step: number): Promise<void> => {
        const selectedOrder = parameters.selectedOrder;

        if (selectedOrder === null || isAllPortfoliosSelected) {
            return;
        }

        const order = parameters.orders.find(x => x.internalId === selectedOrder.internalId);

        if (order === undefined) {
            return;
        }

        order.sizeStep = step;
        order.setChangedState();

        await parameters.updateOrderInSelectedPortfolio(false, false);
    }

    const isDisabled =
        isAllPortfoliosSelected ||
        parameters.selectedOrder === null ||
        parameters.selectedOrder.state === OrderState.Archived;

    return (
        <StepChangeSelector
            selectedStep={stepValue}
            setSelectedStep={setStepValue}
            stepOptions={[1, 0.1, 0.01, 0.001]}
            disabled={isDisabled}
            buttonContent="L"
            previousTooltipText="Reduce order size"
            nextTooltipText="Increase order size"
            onChange={handleChange}
            onStepSelectionChange={handleStepSelectionChange}
            selectorWidth={70}
        />
    );
}
